import * as THREE from 'three';
import { ROAD_ASPHALT_METERS_PER_TILE } from '../utils/constants';

const UP = new THREE.Vector3(0, 1, 0);

/**
 * Interpolate numeric control array (length N) for parameter u in [0,1] along polyline of N points.
 */
export function interpAlongControls(values: number[], u: number): number {
  const n = values.length;
  if (n === 0) return 0;
  if (n === 1) return values[0]!;
  const t = u * (n - 1);
  const i = Math.min(Math.floor(t), n - 2);
  const f = t - i;
  const a = values[i]!;
  const b = values[i + 1]!;
  return a + f * (b - a);
}

/**
 * Banked ribbon: left/right vertices tilt with `bankDeg` (positive = right edge higher in +right frame).
 */
export function ribbonBankedFromCenterlinePath(
  path: THREE.Vector3[],
  widthAt: number[],
  bankDegAt: number[],
  yOffset: number,
): THREE.BufferGeometry {
  const n = path.length;
  if (n < 2 || widthAt.length !== n || bankDegAt.length !== n) {
    return new THREE.BufferGeometry();
  }

  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  let dist = 0;

  for (let i = 0; i < n; i++) {
    const p = path[i]!;
    const w = widthAt[i]!;
    const bankRad = THREE.MathUtils.degToRad(bankDegAt[i]!);

    let tan: THREE.Vector3;
    if (i === 0) tan = path[1]!.clone().sub(path[0]!);
    else if (i === n - 1) tan = path[n - 1]!.clone().sub(path[n - 2]!);
    else tan = path[i + 1]!.clone().sub(path[i - 1]!);
    tan.y = 0;
    if (tan.lengthSq() < 1e-10) tan.set(1, 0, 0);
    tan.normalize();

    let right = new THREE.Vector3().crossVectors(UP, tan);
    if (right.lengthSq() < 1e-10) right.set(1, 0, 0);
    else right.normalize();

    const edge = right
      .clone()
      .multiplyScalar(Math.cos(bankRad))
      .add(UP.clone().multiplyScalar(Math.sin(bankRad)));

    const hw = w * 0.5;
    const pY = p.y + yOffset;
    const Lx = p.x - edge.x * hw;
    const Lz = p.z - edge.z * hw;
    const Rx = p.x + edge.x * hw;
    const Rz = p.z + edge.z * hw;
    positions.push(Lx, pY, Lz, Rx, pY, Rz);
    if (i > 0) dist += path[i]!.distanceTo(path[i - 1]!);
    const u = dist / ROAD_ASPHALT_METERS_PER_TILE;
    uvs.push(u, 0, u, 1);
  }

  for (let i = 0; i < n - 1; i++) {
    const a = i * 2;
    indices.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  const uvAttr = new THREE.Float32BufferAttribute(uvs, 2);
  geo.setAttribute('uv', uvAttr);
  geo.setAttribute('uv2', uvAttr.clone());
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

function offsetPathSidewalk(
  path: THREE.Vector3[],
  offset: number,
): THREE.Vector3[] {
  const n = path.length;
  const out: THREE.Vector3[] = [];
  for (let i = 0; i < n; i++) {
    const p = path[i]!;
    let tan: THREE.Vector3;
    if (i === 0) tan = path[1]!.clone().sub(path[0]!);
    else if (i === n - 1) tan = path[n - 1]!.clone().sub(path[n - 2]!);
    else tan = path[i + 1]!.clone().sub(path[i - 1]!);
    tan.y = 0;
    if (tan.lengthSq() < 1e-10) tan.set(1, 0, 0);
    tan.normalize();
    let right = new THREE.Vector3().crossVectors(UP, tan);
    if (right.lengthSq() < 1e-10) right.set(1, 0, 0);
    else right.normalize();
    out.push(p.clone().addScaledVector(right, offset));
  }
  return out;
}

export function sampleCurvePath(
  curve: THREE.Curve<THREE.Vector3>,
  segments: number,
): THREE.Vector3[] {
  const path: THREE.Vector3[] = [];
  for (let i = 0; i <= segments; i++) {
    path.push(curve.getPointAt(i / segments));
  }
  return path;
}

/** Per-sample width/bank from control arrays (same length as curve `points` length). */
export function sampleWidthsAndBanks(
  curve: THREE.CatmullRomCurve3,
  segments: number,
  widthControls: number[],
  bankControls: number[],
): { widthAt: number[]; bankAt: number[] } {
  const nCtrl = curve.points.length;
  if (widthControls.length !== nCtrl || bankControls.length !== nCtrl) {
    const w = widthControls[0] ?? 14;
    const b = bankControls[0] ?? 0;
    const widthAt: number[] = [];
    const bankAt: number[] = [];
    for (let i = 0; i <= segments; i++) {
      widthAt.push(w);
      bankAt.push(b);
    }
    return { widthAt, bankAt };
  }
  const widthAt: number[] = [];
  const bankAt: number[] = [];
  for (let i = 0; i <= segments; i++) {
    const u = i / segments;
    widthAt.push(interpAlongControls(widthControls, u));
    bankAt.push(interpAlongControls(bankControls, u));
  }
  return { widthAt, bankAt };
}

export type BankedRoadStrip = {
  curve: THREE.CatmullRomCurve3;
  widthControls: number[];
  bankControls: number[];
  segments: number;
};

/** Sidewalk ribbons offset from banked center (same path, zero bank for simplicity). */
export function addSidewalksForBankedPath(
  root: THREE.Group,
  path: THREE.Vector3[],
  sideOff: number,
  swW: number,
  swY: number,
  swMat: THREE.Material,
): void {
  for (const sign of [-1, 1] as const) {
    const swPath = offsetPathSidewalk(path, sign * sideOff);
    const flat = path.map((p, i) => {
      const q = swPath[i]!.clone();
      q.y = p.y;
      return q;
    });
    const w = flat.map(() => swW);
    const b = flat.map(() => 0);
    const swGeo = ribbonBankedFromCenterlinePath(flat, w, b, swY);
    root.add(new THREE.Mesh(swGeo, swMat));
  }
}
